// =============================================================================
// domain/coachWindow.ts — the Coach's Report's own date window.
// -----------------------------------------------------------------------------
// ── 🚨 THIS IS NOT THE GLOBAL FILTER, AND IT MUST NEVER BECOME IT ──
//
// The old client says so outright: the leak loop "owns its OWN date window and
// must never ride the global activeDateRange or the analytics-local
// analyticsState — the user moves those."
//
// The reason is the method, not tidiness. A rule is written against a baseline
// measured over a window, and then re-proved over trades since. If the window
// followed the global filter, opening the Trades tab and filtering to last March
// would silently re-measure every baseline on the report against a different
// population — and the trader would read an improvement or a regression that was
// entirely an artefact of a control they moved somewhere else.
//
// ⚠ NO WALL CLOCK HERE (D26). `todayIso` is passed in.
// =============================================================================

export type CoachWindowId = '7d' | '30d' | '90d' | 'ytd' | 'all';

export const COACH_WINDOWS: readonly { id: CoachWindowId; label: string }[] = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
  { id: 'ytd', label: 'YTD' },
  { id: 'all', label: 'All' },
];

/**
 * ⚠ 30 DAYS, NOT ALL. The read behind this report carries the fill detail on
 * every trade, so `all` is the widest read in the client — see
 * `io/sync/leakUniverse.ts`. A default of `all` would make every visit to the
 * tab pull the whole book, and nobody would ever notice because it would simply
 * feel slow.
 */
export const DEFAULT_COACH_WINDOW: CoachWindowId = '30d';

const DAY_MS = 86_400_000;

const iso = (d: Date): string =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

/**
 * The inclusive start date, or `null` for the whole book.
 *
 * ⚠ `7d` REACHES BACK SIX DAYS, not seven. A seven-day window that includes
 * today is today plus the six before it; subtracting seven would quietly make
 * every "7d" figure an eight-day one, and the same off-by-one would run through
 * 30d and 90d — which are the windows a baseline gets frozen against.
 */
export function coachWindowStart(
  id: CoachWindowId, todayIso: string,
): string | null {
  if (id === 'all') return null;

  const today = new Date(`${todayIso}T00:00:00Z`);
  if (Number.isNaN(today.getTime())) return null;

  if (id === 'ytd') return `${today.getUTCFullYear()}-01-01`;

  const days = id === '7d' ? 7 : id === '30d' ? 30 : 90;
  return iso(new Date(today.getTime() - (days - 1) * DAY_MS));
}

/**
 * How far back the READ must reach, which is not the same as the window.
 *
 * ── 🚨 A RULE'S WINDOW IS ITS OWN, AND IT DOES NOT MOVE ──
 *
 * Prove measures a rule from the day it was written until today. That window
 * belongs to the rule, not to the 7d/30d/90d buttons — the old client proves
 * against the whole book for exactly this reason.
 *
 * This client reads from the database by date, so a rule older than the selected
 * window would be re-measured against a TRUNCATED population. Josh's two live
 * rules start 2026-07-19; on a 7d view the read would return the last week and
 * Prove would report a sample of four where the real one is dozens — which reads
 * as though he had almost stopped trading, and every verdict below it would be
 * drawn from that.
 *
 * ⚠ IT WIDENS THE READ, NEVER THE REPORT. Spot it still slices to the selected
 * window in memory. The only thing that changes is how much was fetched.
 *
 * @param windowStart what the picker asked for; null means the whole book.
 * @param needs every OTHER date the screen must have in hand — the earliest an
 *   active rule reaches back to, and the start of the prior window the
 *   period-over-period strip compares against. Null entries are "nothing needed
 *   here" and impose no constraint.
 */
export function readSince(
  windowStart: string | null, ...needs: readonly (string | null | undefined)[]
): string | null {
  // ⚠ THE TWO KINDS OF NULL MEAN OPPOSITE THINGS, and treating them alike is a
  // bug a test caught on the first run. `windowStart === null` is the **All**
  // window — no lower bound, fetch everything. A null in `needs` is "nothing
  // needs anything earlier", which is no constraint at all, so the window still
  // decides. Folding them together made every default 30-day visit read the
  // whole book.
  if (windowStart === null) return null;
  let earliest = windowStart;
  for (const need of needs) {
    if (need != null && need < earliest) earliest = need;
  }
  return earliest;
}

/**
 * The window immediately before the selected one, same length — the population
 * the period-over-period strip compares against.
 *
 * ⚠ NULL FOR `ytd` AND `all`, and that is the old client's answer too
 * (app/index.html:41973 leaves `prevCutoff` null for both). "Year to date" has
 * no matched prior window — the equivalent stretch of last year is a different
 * number of trading days, and in January it is a handful of them. Comparing
 * against it would put a ▲ or a ▼ on noise. `all` has nothing before it at all.
 *
 * ⚠ `endExclusive` IS THE CURRENT WINDOW'S START, so the two never overlap by a
 * day. An inclusive end here would count the boundary day in both halves of a
 * comparison whose entire job is to tell them apart.
 */
export interface CoachWindowSpan {
  /** Inclusive. */
  start: string;
  /** Exclusive — the current window's first day. */
  endExclusive: string;
}

export function coachPriorWindow(
  id: CoachWindowId, todayIso: string,
): CoachWindowSpan | null {
  if (id === 'all' || id === 'ytd') return null;
  const start = coachWindowStart(id, todayIso);
  if (start === null) return null;

  const from = new Date(`${start}T00:00:00Z`);
  if (Number.isNaN(from.getTime())) return null;

  const days = id === '7d' ? 7 : id === '30d' ? 30 : 90;
  return { start: iso(new Date(from.getTime() - days * DAY_MS)), endExclusive: start };
}

/**
 * Trades on or after `start`, which is what makes the report's own headline
 * ("over the last 30 days") true.
 *
 * ── 🚨 THIS EXISTS BECAUSE THE SLICE WAS MISSING AND TWO COMMENTS SAID IT WAS NOT ──
 *
 * `readSince` widens the READ past the window — for an active rule, and now for
 * the prior window too. Its own doc says "it widens the read, NEVER the report:
 * Spot it still slices to the selected window in memory", and `useLeakReport`
 * repeats the claim. Nothing did. Every trade the widened read returned flowed
 * into Spot it, the four lenses and the AI verdict payload, so a trader with a
 * rule written in July read a "7d" report computed over seven WEEKS — labelled
 * as seven days, on every heading and in the prompt sent to the model.
 *
 * The old client slices (app/index.html:41984, `dOf(t) >= cutoff`); this is that
 * line, and the comments above are now descriptions of code that runs.
 */
export function tradesInCoachWindow<T extends { date: string }>(
  trades: readonly T[], start: string | null,
): readonly T[] {
  if (start === null) return trades;
  return trades.filter((t) => String(t.date ?? '') >= start);
}

/** Trades inside a prior window — `[start, endExclusive)`. */
export function tradesInSpan<T extends { date: string }>(
  trades: readonly T[], span: CoachWindowSpan | null,
): readonly T[] {
  if (!span) return [];
  return trades.filter((t) => {
    const d = String(t.date ?? '');
    return d >= span.start && d < span.endExclusive;
  });
}

/** "the last 30 days" / "this year so far" / "your whole book". */
export function coachWindowPhrase(id: CoachWindowId): string {
  switch (id) {
    case '7d': return 'the last 7 days';
    case '30d': return 'the last 30 days';
    case '90d': return 'the last 90 days';
    case 'ytd': return 'this year so far';
    default: return 'your whole book';
  }
}
